import React from 'react'
import styled from 'styled-components'

import Avatar from '../Avatar'
import { fastTransition, shadows } from '../../styles/effects'
import { ICompanyMember } from '../../common/models/Company'

interface IProps {
  member: ICompanyMember
  size?: number
  hovered?: boolean
  pinned?: boolean
  ariaLabel?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onClick?: () => void
  className?: string
}

const InsightsMatrixMemberDot = React.forwardRef<HTMLButtonElement, IProps>(
  (
    {
      member,
      size = 36,
      hovered,
      pinned,
      ariaLabel,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      onClick,
      className,
    },
    ref
  ) => (
    <HitTarget
      ref={ref}
      type="button"
      className={className}
      aria-label={ariaLabel}
      aria-pressed={pinned}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
    >
      <DotFrame $size={size} $hovered={!!hovered} $pinned={!!pinned}>
        <Avatar user={member} />
      </DotFrame>
    </HitTarget>
  )
)

InsightsMatrixMemberDot.displayName = 'InsightsMatrixMemberDot'

export default InsightsMatrixMemberDot

const HitTarget = styled.button`
  width: 44px;
  height: 44px;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: none;
  }
`

const DotFrame = styled.div<{
  $size: number
  $hovered: boolean
  $pinned: boolean
}>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.lightest};
  border: 2px
    ${({ $hovered, $pinned }) =>
      $pinned ? 'solid' : $hovered ? 'dashed' : 'solid'}
    ${({ theme, $hovered, $pinned }) =>
      $hovered || $pinned ? theme.colors.primary : theme.colors.lightest};
  ${({ $pinned }) =>
    $pinned &&
    `
    border-width: 3px;
    transform: scale(1.08);
  `}
  transition: transform ${fastTransition}, border-color ${fastTransition},
    border-width ${fastTransition}, box-shadow ${fastTransition};
  box-shadow: ${({ $hovered, $pinned }) =>
    $hovered || $pinned ? shadows.depthThree : shadows.depthOne};

  > figure {
    width: 100%;
    height: 100%;
    margin: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${HitTarget}:focus-visible & {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 4px;
    border-radius: 50%;
  }
`
